/**
 * PlanningCalendar — day view (Resource × Time). Controlled: receives the day,
 * items and data from the container; reports create/edit/move via callbacks.
 * Keeps its own day-range + interval controls and drag/resize behaviour.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type PlannerResource,
  type PlannerService,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { SLOT_PX, STATE_COLORS, hm, stateInfo } from './plannerShared';
import type { CreateCtx } from './ScheduledForm';

const selClass = 'rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

interface DragState { id: string; mode: 'move' | 'resize'; startY: number; origStart: Date; origEnd: Date }

export default function PlanningCalendar({ date, resources, services, tax, items, canManage, uncoveredItemIds, onCreate, onEdit, onCommitMove }: {
  date: Date;
  resources: PlannerResource[];
  services: PlannerService[];
  tax: PlannerTaxonomies | null;
  items: PlannerScheduledItem[];
  canManage: boolean;
  uncoveredItemIds?: Set<string>;
  onCreate: (ctx: CreateCtx) => void;
  onEdit: (item: PlannerScheduledItem) => void;
  onCommitMove: (id: string, startISO: string, endISO: string) => void;
}) {
  const [dayStart, setDayStart] = useState(8);
  const [dayEnd, setDayEnd] = useState(20);
  const [interval, setIntervalMin] = useState(30);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [previewMin, setPreviewMin] = useState<{ start: number; dur: number } | null>(null);
  const previewRef = useRef<{ start: number; dur: number } | null>(null);
  const movedRef = useRef(false);

  const activeResources = useMemo(() => resources.filter((r) => r.active), [resources]);
  const slots = Math.max(1, Math.round(((dayEnd - dayStart) * 60) / interval));
  const containerHeight = slots * SLOT_PX;
  const minToTop = (min: number) => (min / interval) * SLOT_PX;
  const itemStartMin = (it: PlannerScheduledItem) => { const s = new Date(it.start); return s.getHours() * 60 + s.getMinutes() - dayStart * 60; };
  const itemDurMin = (it: PlannerScheduledItem) => Math.max(interval, (new Date(it.end).getTime() - new Date(it.start).getTime()) / 60000);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const deltaSlots = Math.round((e.clientY - drag.startY) / SLOT_PX);
      if (deltaSlots !== 0) movedRef.current = true;
      const origStartMin = drag.origStart.getHours() * 60 + drag.origStart.getMinutes() - dayStart * 60;
      const origDurMin = Math.max(interval, (drag.origEnd.getTime() - drag.origStart.getTime()) / 60000);
      let next: { start: number; dur: number };
      if (drag.mode === 'move') {
        next = { start: Math.max(0, Math.min(slots * interval - origDurMin, origStartMin + deltaSlots * interval)), dur: origDurMin };
      } else {
        next = { start: origStartMin, dur: Math.max(interval, Math.min(slots * interval - origStartMin, origDurMin + deltaSlots * interval)) };
      }
      previewRef.current = next;
      setPreviewMin(next);
    };
    const onUp = () => {
      const moved = movedRef.current;
      const current = drag;
      const preview = previewRef.current;
      setDrag(null);
      setPreviewMin(null);
      previewRef.current = null;
      movedRef.current = false;
      if (!moved || !current || !preview) return;
      const base = new Date(date);
      base.setHours(dayStart, 0, 0, 0);
      const newStart = new Date(base.getTime() + preview.start * 60000);
      const newEnd = new Date(newStart.getTime() + preview.dur * 60000);
      onCommitMove(current.id, newStart.toISOString(), newEnd.toISOString());
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const startDrag = (e: React.MouseEvent, it: PlannerScheduledItem, mode: 'move' | 'resize') => {
    if (!canManage) return;
    e.stopPropagation();
    e.preventDefault();
    movedRef.current = false;
    setDrag({ id: it._id, mode, startY: e.clientY, origStart: new Date(it.start), origEnd: new Date(it.end) });
  };

  const onColumnClick = (resourceId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (!canManage || drag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const slotIndex = Math.max(0, Math.min(slots - 1, Math.floor((e.clientY - rect.top) / SLOT_PX)));
    const start = new Date(date);
    start.setHours(dayStart, 0, 0, 0);
    start.setMinutes(start.getMinutes() + slotIndex * interval);
    onCreate({ resourceId, start });
  };

  const timeLabels = Array.from({ length: slots }, (_, i) => {
    const m = dayStart * 60 + i * interval;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>De</span>
        <select className={selClass} value={dayStart} onChange={(e) => setDayStart(Number(e.target.value))}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
        </select>
        <span>a</span>
        <select className={selClass} value={dayEnd} onChange={(e) => setDayEnd(Number(e.target.value))}>
          {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
        </select>
        <span>cada</span>
        <select className={selClass} value={interval} onChange={(e) => setIntervalMin(Number(e.target.value))}>
          {[15, 30, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
        </select>
        {canManage && <span className="text-xs text-gray-400 ml-auto">Clic = crear · arrastrar = mover · borde inferior = duración</span>}
      </div>

      {activeResources.length === 0 ? (
        <p className="text-sm text-gray-500">No hay recursos activos. Crea recursos en la pestaña Recursos.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <div className="flex min-w-fit select-none">
            <div className="shrink-0 w-16 border-r border-gray-200">
              <div className="h-10 border-b border-gray-200" />
              <div className="relative" style={{ height: containerHeight }}>
                {timeLabels.map((t, i) => (
                  <div key={t} className="absolute left-0 right-0 text-[10px] text-gray-400 px-1 -translate-y-1/2" style={{ top: i * SLOT_PX }}>{t}</div>
                ))}
              </div>
            </div>
            {activeResources.map((r) => (
              <div key={r._id} className="shrink-0 w-44 border-r border-gray-100 last:border-r-0">
                <div className="h-10 border-b border-gray-200 px-2 flex items-center text-xs font-medium text-gray-700 truncate" title={r.name}>{r.name}</div>
                <div className="relative" style={{ height: containerHeight }} onClick={(e) => onColumnClick(r._id, e)}>
                  {Array.from({ length: slots }, (_, i) => (
                    <div key={i} className="absolute left-0 right-0 border-b border-gray-50" style={{ top: i * SLOT_PX, height: SLOT_PX }} />
                  ))}
                  {items.filter((it) => it.resourceId === r._id).map((it) => {
                    const isDragged = drag?.id === it._id && previewMin;
                    const startMin = isDragged ? previewMin!.start : itemStartMin(it);
                    const durMin = isDragged ? previewMin!.dur : itemDurMin(it);
                    const top = minToTop(startMin);
                    const height = Math.max(SLOT_PX - 2, minToTop(durMin) - 2);
                    const st = stateInfo(tax, it.stateNodeId);
                    const uncovered = uncoveredItemIds?.has(it._id) || false;
                    // Uncovered items override the state color: the warning is
                    // more important to surface than the workflow state.
                    const color = uncovered ? 'bg-amber-500' : (st && STATE_COLORS[st.id]) || 'bg-vida-600';
                    const svc = services.find((s) => s._id === it.serviceId);
                    const labelStart = new Date(new Date(date).setHours(dayStart, 0, 0, 0) + startMin * 60000);
                    return (
                      <div
                        key={it._id}
                        onMouseDown={(e) => startDrag(e, it, 'move')}
                        onClick={(e) => { e.stopPropagation(); if (!movedRef.current) onEdit(it); }}
                        className={`absolute left-1 right-1 ${color} text-white rounded-md px-1.5 py-0.5 text-[11px] overflow-hidden shadow-sm hover:opacity-90 cursor-move ${it.alarmAt ? 'ring-1 ring-white/70' : ''}`}
                        style={{ top: top + 1, height }}
                        title={uncovered ? 'El empleado asignado no tiene presencia planificada para este horario' : undefined}
                      >
                        <div className="font-medium truncate">{svc?.name || 'Servicio'}</div>
                        <div className="truncate opacity-90">{hm(labelStart)} · {it.clientLabel || ''}</div>
                        {uncovered && (
                          <div className="truncate text-[10px] opacity-95">⚠ Sin presencia asignada</div>
                        )}
                        {canManage && (
                          <div onMouseDown={(e) => startDrag(e, it, 'resize')} className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
